export type AutocompleteState = {
  token?: Record<string, any>;
  tokenCreatedAtTime?: number;
  tokenMaxAge: number;
  errorCount: number;
};

export const useAutocompleteStore = defineStore('autocomplete', {
  state: (): AutocompleteState => ({
    token: undefined,
    tokenCreatedAtTime: undefined,
    tokenMaxAge: 60 * 3,
    errorCount: 0,
  }),
  actions: {
    async retrieveToken(): Promise<any> {
      // Skip further API calls if we ran into API quote errors or
      // similar, we can only know after the first attempt of using it
      if (this.errorCount > 0) {
        return null;
      }

      const { $api } = useNuxtApp();
      logger.debug('Retrieving autocomplete token...');

      if (this.getToken) {
        logger.debug('Reusing autocomplete token');
        return this.getToken;
      }

      try {
        const response = await $api.hybris.autoComplete.startSession();

        logger.debug('Retrieved autocomplete token');
        this.token = response.data.sessionToken;
        this.tokenCreatedAtTime = new Date().getTime();

        return response.data.sessionToken;
      } catch (error: any) {
        if (!error?.response?.data?.errors?.[0]?.reason?.includes('quota-exhausted')) {
          logger.error('Error while retrieving autocomplete token', error);
        }

        // Getting error.type `AddressAutocompleteWebServiceException` on empty quota here,
        // could be handled more gracefully in the future
        this.errorCount++;

        return Promise.resolve(error);
      }
    },

    async getResults({ address, addressType }: { address: any; addressType: any }) {
      // Skip further API calls if we ran into API quote errors or
      // similar, we can only know after the first attempt of using it
      if (this.errorCount > 0) {
        return null;
      }

      const { $api } = useNuxtApp();
      logger.debug('Retrieving autocomplete results...');

      try {
        const response = await $api.hybris.autoComplete.getResults(address, addressType, await this.retrieveToken());
        logger.debug('Retrieved autocomplete results');

        return response.data.addresses;
      } catch (error) {
        logger.error('Error while retrieving autocomplete results', error);

        this.errorCount++;

        return Promise.resolve(error);
      }
    },
  },
  getters: {
    getToken(state): any {
      if (!state.token) {
        return null;
      }

      return this.getTokenRemaining > 0 ? state.token : null;
    },

    getTokenAge(state): any {
      if (!state.token || !state.tokenCreatedAtTime) {
        return -1;
      }

      return (new Date().getTime() - state.tokenCreatedAtTime) / 1000;
    },

    getTokenRemaining(state): any {
      if (this.getTokenAge <= 0) {
        return 0;
      }

      return Math.max(0, state.tokenMaxAge - this.getTokenAge);
    },

    getAutoCompleteEnabled(state): any {
      return useMainStore().getFeature('autoCompleteEnabled') && !state.errorCount;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAutocompleteStore, import.meta.hot));
}
